import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface SimRun {
  id: number;
  pfId: number;
  type: 'daily' | 'intraday';
  fromDate: string;
  toDate: string;
  orderDate: string | null;
  params: string | null;
  enabledStrategies: string[] | null;
  symbolCount: number;
  signalCount: number;
  buyCount: number;
  sellCount: number;
  holdCount: number;
  status: string;
  createdAt: string | null;
}

interface SimResult {
  id: number;
  runId: number;
  prodId: string;
  prodNm: string | null;
  decision: 'BUY' | 'SELL' | 'HOLD' | null;
  buyCount: number;
  sellCount: number;
  holdCount: number;
  wei: number;
  lastClose: number;
  confidence: number;
  signals: string | null;
}

interface BacktestOrder {
  id: number;
  runId: number;
  d: string;
  pfId: number;
  prodId: string;
  prodNm: string | null;
  ls: number;
  ex: number;
  wei: number;
  qty: number;
  prc: number;
  amt: number;
  decision: string | null;
  createdAt: string | null;
}

const decisionStyle = (d: string | null) => {
  if (d === 'BUY') return { bg: '#fee2e2', color: '#dc2626' };
  if (d === 'SELL') return { bg: '#dbeafe', color: '#2563eb' };
  return { bg: '#f3f4f6', color: '#6b7280' };
};

const SimHistory: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const [runs, setRuns] = useState<SimRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'daily' | 'intraday'>('ALL');
  const [selectedRun, setSelectedRun] = useState<SimRun | null>(null);
  const [results, setResults] = useState<SimResult[]>([]);
  const [backtestOrders, setBacktestOrders] = useState<BacktestOrder[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const axios = getMainAxios();
      const params: any = { limit: 200 };
      if (typeFilter !== 'ALL') params.type = typeFilter;
      const res = await axios.get('/api/v1/quant/sim-runs', { params });
      setRuns(res.data as SimRun[]);
    } catch (err: any) {
      setError(err.response?.data?.error || '이력 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const fetchDetail = async (runId: number) => {
    setDetailLoading(true);
    try {
      const axios = getMainAxios();
      const [detailRes, btoRes] = await Promise.all([
        axios.get(`/api/v1/quant/sim-runs/${runId}`),
        axios.get('/api/v1/quant/backtest-orders', { params: { runId } }),
      ]);
      setSelectedRun(detailRes.data.run);
      setResults(detailRes.data.results || []);
      setBacktestOrders(btoRes.data || []);
    } catch (err: any) {
      alert(err.response?.data?.error || '상세 조회 실패');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (run: SimRun) => {
    if (!confirm(`run #${run.id} (${run.type}, ${run.createdAt}) 을(를) 삭제하시겠습니까?`)) return;
    try {
      const axios = getMainAxios();
      await axios.delete(`/api/v1/quant/sim-runs/${run.id}`);
      if (selectedRun?.id === run.id) {
        setSelectedRun(null);
        setResults([]);
        setBacktestOrders([]);
      }
      fetchRuns();
    } catch (err: any) {
      alert(err.response?.data?.error || '삭제 실패');
    }
  };

  const runCols = useMemo<ColDef<SimRun>[]>(() => [
    { field: 'id', headerName: 'ID', width: 70, cellStyle: { textAlign: 'right' } },
    {
      field: 'type', headerName: '타입', width: 100, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => {
        const isDaily = p.value === 'daily';
        return (
          <span style={{
            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
            backgroundColor: isDaily ? '#dbeafe' : '#fef3c7',
            color: isDaily ? '#1e40af' : '#92400e',
          }}>{isDaily ? '일봉' : '인트라데이'}</span>
        );
      },
    },
    { field: 'pfId', headerName: 'pfId', width: 80, cellStyle: { textAlign: 'right' } },
    { field: 'fromDate', headerName: '시작일', width: 110, cellStyle: { textAlign: 'center' } },
    { field: 'toDate', headerName: '종료일', width: 110, cellStyle: { textAlign: 'center' } },
    { field: 'orderDate', headerName: '주문일', width: 110, cellStyle: { textAlign: 'center' } },
    { field: 'symbolCount', headerName: '종목', width: 75, cellStyle: { textAlign: 'right' } },
    { field: 'signalCount', headerName: '신호', width: 75, cellStyle: { textAlign: 'right' } },
    {
      field: 'buyCount', headerName: 'BUY', width: 70, cellStyle: { textAlign: 'right', color: '#dc2626', fontWeight: 600 },
    },
    {
      field: 'sellCount', headerName: 'SELL', width: 70, cellStyle: { textAlign: 'right', color: '#2563eb', fontWeight: 600 },
    },
    {
      field: 'holdCount', headerName: 'HOLD', width: 70, cellStyle: { textAlign: 'right', color: '#6b7280' },
    },
    {
      field: 'enabledStrategies', headerName: '전략', width: 180,
      valueFormatter: p => Array.isArray(p.value) ? p.value.join(',') : '',
      cellStyle: { fontFamily: 'monospace', fontSize: '11px' },
    },
    { field: 'status', headerName: '상태', width: 90, cellStyle: { textAlign: 'center' } },
    {
      field: 'createdAt', headerName: '실행시간', width: 160, cellStyle: { textAlign: 'center' },
      valueFormatter: p => p.value ? String(p.value).replace('T', ' ').substring(0, 19) : '',
    },
    {
      headerName: '작업', width: 140, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => (
        <span>
          <button onClick={() => fetchDetail(p.data.id)}
            style={{ padding: '2px 8px', marginRight: '4px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>
            상세
          </button>
          <button onClick={() => handleDelete(p.data)}
            style={{ padding: '2px 8px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>
            삭제
          </button>
        </span>
      ),
    },
  ], []);

  const resultCols = useMemo<ColDef<SimResult>[]>(() => [
    { field: 'prodId', headerName: '종목코드', width: 90, cellStyle: { textAlign: 'center', fontFamily: 'monospace' } },
    { field: 'prodNm', headerName: '종목명', width: 140 },
    {
      field: 'decision', headerName: '결정', width: 80, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => {
        const s = decisionStyle(p.value);
        return <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, backgroundColor: s.bg, color: s.color }}>{p.value || '-'}</span>;
      },
    },
    { field: 'buyCount', headerName: 'BUY', width: 65, cellStyle: { textAlign: 'right', color: '#dc2626', fontWeight: 600 } },
    { field: 'sellCount', headerName: 'SELL', width: 65, cellStyle: { textAlign: 'right', color: '#2563eb', fontWeight: 600 } },
    { field: 'holdCount', headerName: 'HOLD', width: 65, cellStyle: { textAlign: 'right', color: '#6b7280' } },
    {
      field: 'wei', headerName: '비중', width: 75, cellStyle: { textAlign: 'right' },
      valueFormatter: p => p.value != null ? p.value.toFixed(1) : '',
    },
    {
      field: 'confidence', headerName: '신뢰도', width: 80, cellStyle: { textAlign: 'right' },
      valueFormatter: p => p.value != null ? (p.value * 100).toFixed(1) : '',
    },
    {
      field: 'lastClose', headerName: '종가', width: 100, cellStyle: { textAlign: 'right' },
      valueFormatter: p => p.value != null ? Number(p.value).toLocaleString() : '',
    },
    {
      field: 'signals', headerName: '전략별 신호', flex: 1, minWidth: 300,
      cellStyle: { fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' },
      valueFormatter: p => {
        if (!p.value) return '';
        try {
          const arr = JSON.parse(p.value);
          return arr.map((s: any) => `${s.strategyName}:${s.signal === 1 ? 'B' : s.signal === 2 ? 'S' : 'H'}`).join(' ');
        } catch { return p.value; }
      },
    },
  ], []);

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: 600 }}>시뮬레이션 이력 ({runs.length}건)</span>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)}
            style={{ padding: '4px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '3px' }}>
            <option value="ALL">전체</option>
            <option value="daily">일봉</option>
            <option value="intraday">인트라데이</option>
          </select>
        </div>
        <button onClick={fetchRuns}
          style={{ padding: '5px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
          새로고침
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: '10px', padding: '8px 12px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '4px', color: '#991b1b', fontSize: '12px' }}>
          {error}
        </div>
      )}

      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden', marginBottom: '12px' }}>
        <div className="ag-theme-quartz" style={{ height: 320, width: '100%' }}>
          <AgGridReact<SimRun> {...copyMenu.gridProps}
            rowData={runs}
            columnDefs={runCols}
            defaultColDef={{ resizable: true, sortable: true, filter: false, cellStyle: { fontSize: '12px' } }}
            getRowId={p => String(p.data.id)}
            animateRows={true}
            loading={loading}
            onRowClicked={e => e.data && fetchDetail(e.data.id)}
          />
        </div>
      </div>

      {selectedRun && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', backgroundColor: '#dbeafe', borderBottom: '1px solid #93c5fd', fontSize: '12px' }}>
            <strong style={{ color: '#1e40af' }}>run #{selectedRun.id}</strong>
            {' · '}{selectedRun.type === 'daily' ? '일봉' : '인트라데이'}
            {' · '}{selectedRun.fromDate} ~ {selectedRun.toDate}
            {selectedRun.orderDate && <> · 주문일 {selectedRun.orderDate}</>}
            {' · '}종목 {selectedRun.symbolCount}
            {' · '}BUY <span style={{ color: '#dc2626', fontWeight: 600 }}>{selectedRun.buyCount}</span>
            {' / SELL '}<span style={{ color: '#2563eb', fontWeight: 600 }}>{selectedRun.sellCount}</span>
            {' / HOLD '}<span style={{ color: '#6b7280' }}>{selectedRun.holdCount}</span>
            {selectedRun.params && (
              <details style={{ marginTop: '6px' }}>
                <summary style={{ cursor: 'pointer', color: '#6b7280' }}>파라미터 JSON</summary>
                <pre style={{ margin: '4px 0 0', padding: '6px 8px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '3px', fontSize: '11px', overflow: 'auto' }}>
                  {(() => { try { return JSON.stringify(JSON.parse(selectedRun.params!), null, 2); } catch { return selectedRun.params; } })()}
                </pre>
              </details>
            )}
          </div>
          <div className="ag-theme-quartz" style={{ height: 360, width: '100%' }}>
            <AgGridReact<SimResult> {...copyMenu.gridProps}
              rowData={results}
              columnDefs={resultCols}
              defaultColDef={{ resizable: true, sortable: true, filter: false, cellStyle: { fontSize: '12px' } }}
              getRowId={p => String(p.data.id)}
              animateRows={true}
              loading={detailLoading}
            />
          </div>
          {backtestOrders.length > 0 && (
            <>
              <div style={{ padding: '8px 16px', backgroundColor: '#fef3c7', borderTop: '1px solid #fde68a', borderBottom: '1px solid #fde68a', fontSize: '11px', color: '#92400e', fontWeight: 600 }}>
                백테스트 주문 ({backtestOrders.length}건) — 실주문과 분리된 시뮬레이션 전용 기록
              </div>
              <div className="ag-theme-quartz" style={{ height: 240, width: '100%' }}>
                <AgGridReact<BacktestOrder> {...copyMenu.gridProps}
                  rowData={backtestOrders}
                  columnDefs={[
                    { field: 'd', headerName: '주문일', width: 110, cellStyle: { textAlign: 'center' } },
                    { field: 'prodId', headerName: '종목코드', width: 90, cellStyle: { textAlign: 'center', fontFamily: 'monospace' } },
                    { field: 'prodNm', headerName: '종목명', width: 140 },
                    {
                      field: 'decision', headerName: '결정', width: 80, cellStyle: { textAlign: 'center' },
                      cellRenderer: (p: any) => {
                        const s = decisionStyle(p.value);
                        return <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, backgroundColor: s.bg, color: s.color }}>{p.value || '-'}</span>;
                      },
                    },
                    {
                      field: 'ex', headerName: 'EX', width: 70, cellStyle: { textAlign: 'center' },
                      valueFormatter: p => p.value === 1 ? '진입(E)' : p.value === -1 ? '청산(X)' : '-',
                    },
                    {
                      field: 'wei', headerName: '비중', width: 75, cellStyle: { textAlign: 'right' },
                      valueFormatter: p => p.value != null ? p.value.toFixed(1) : '',
                    },
                    {
                      field: 'prc', headerName: '체결가(종가)', width: 110, cellStyle: { textAlign: 'right' },
                      valueFormatter: p => p.value != null ? Number(p.value).toLocaleString() : '',
                    },
                    { field: 'ls', headerName: 'LS', width: 60, cellStyle: { textAlign: 'center' },
                      valueFormatter: p => p.value === 1 ? 'L' : p.value === -1 ? 'S' : '-',
                    },
                    {
                      field: 'createdAt', headerName: '기록시각', width: 160, cellStyle: { textAlign: 'center' },
                      valueFormatter: p => p.value ? String(p.value).replace('T', ' ').substring(0, 19) : '',
                    },
                  ]}
                  defaultColDef={{ resizable: true, sortable: true, filter: false, cellStyle: { fontSize: '12px' } }}
                  getRowId={p => String(p.data.id)}
                />
              </div>
            </>
          )}
        </div>
      )}
      {copyMenu.menu}
    </div>
  );
};

export default SimHistory;
